import React, { useMemo, useState } from "react";
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip, Popover, Card, CardContent, Divider } from "@mui/material";
import { ChevronLeft, ChevronRight, Today, Close } from "@mui/icons-material";
import { METALS_CALENDAR_COLORS, ESTADO_ORO_COLORS, BACKGROUND_COLORS, STATUS_COLORS } from "../../style/colors.js";
import { 
  toDateKey, 
  normalizeDailyNeeds, 
  calculateDayAggregates, 
  calculateDimensions,
  calculateDailyAvailability
} from "../../services/utils/calendarUtils.js";

export default function ComponentMetalsCalendar({ 
  baseStock, 
  details, 
  excelData,
  StockTotalOro,
  StockTotalPlatino,
  title = "Calendario de Metales", 
  compact = false, 
  size = "md", 
  fixedHeight, 
  showTitle = true, 
  cellWidth 
}) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarRef, setCalendarRef] = useState(null);

  const { dims, cellW } = calculateDimensions(size, compact, fixedHeight, showTitle, cellWidth);

  const dailyNeeds = useMemo(() => normalizeDailyNeeds(details), [details]);

  const dayAggregates = useMemo(() => {
    if (excelData && excelData.length > 0 && StockTotalOro && StockTotalPlatino) {
      return calculateDailyAvailability(cursor, StockTotalOro, StockTotalPlatino, excelData);
    } else {
      return calculateDayAggregates(cursor, baseStock, dailyNeeds);
    }
  }, [cursor, StockTotalOro, StockTotalPlatino, excelData, baseStock, dailyNeeds]);

  const headerMonth = cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" }).replace(/^\w/, c => c.toUpperCase());
  const { matrix, data: dataMap } = dayAggregates;

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const isToday = (date) => toDateKey(date) === toDateKey(today);
  const inCurrentMonth = (date) => date.getMonth() === cursor.getMonth();

  const WeekdayHeader = () => (
    <TableHead>
      <TableRow>
        {["L", "M", "X", "J", "V", "S", "D"].map((day, index) => (
          <TableCell key={day} sx={{
            backgroundColor: METALS_CALENDAR_COLORS.DARK,
            color: METALS_CALENDAR_COLORS.HEADER_TEXT,
            fontWeight: compact ? 600 : "bold",
            textAlign: "center",
            px: compact ? 0.5 : 1,
            py: compact ? 0.5 : 1,
            borderColor: METALS_CALENDAR_COLORS.DAY_BORDER,
            ...(index === 0 && { borderTopLeftRadius: 3}),
            ...(index === 6 && { borderTopRightRadius: 3 }),
          }}>
            {day}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const DayCell = ({ date }) => {
    const key = toDateKey(date);
    const info = dataMap[key];
    const muted = !inCurrentMonth(date);
    const borderStyle = isToday(date) 
      ? `2px solid ${METALS_CALENDAR_COLORS.TODAY_BORDER}` 
      : `1px solid ${METALS_CALENDAR_COLORS.DAY_BORDER}`;

    // Usar los nuevos datos si están disponibles
    const oroDisponible = info?.oroDisponible;
    const platinoDisponible = info?.platinoDisponible;
    const requeridoOro = info?.requeridoOro || 0;
    const requeridoPlatino = info?.requeridoPlatino || 0;

    // Determinar color de fondo según disponibilidad de oro
    const getBackgroundColor = () => {
      if (muted) return METALS_CALENDAR_COLORS.DAY_BG_ALT;
      if (oroDisponible === null) return METALS_CALENDAR_COLORS.DAY_BG; // Fondo blanco si no se calculó
      if (oroDisponible >= 0) return METALS_CALENDAR_COLORS.DAY_BG_POSITIVE;
      return METALS_CALENDAR_COLORS.DAY_BG_NEGATIVE;
    };

    const handleDayClick = (event) => {
      if (!muted && oroDisponible !== null) {
        setAnchorEl(calendarRef);
        setSelectedDay({
          date,
          oroDisponible,
          platinoDisponible,
          requeridoOro,
          requeridoPlatino,
          stockTotalOro: info?.stockTotalOro || StockTotalOro,
          stockTotalPlatino: info?.stockTotalPlatino || StockTotalPlatino
        });
      }
    };

    if (compact) {
      return (
        <TableCell 
          onClick={handleDayClick}
          sx={{
            verticalAlign: "middle",
            padding: 0.3,
            backgroundColor: getBackgroundColor(),
            border: borderStyle,
            height: "auto",
            width: "auto",
            minWidth: 25,
            textAlign: "center",
            cursor: !muted && oroDisponible !== null ? "pointer" : "default",
            "&:hover": !muted && oroDisponible !== null ? {
              backgroundColor: oroDisponible >= 0 ? STATUS_COLORS.SUCCESS_LIGHT : STATUS_COLORS.ERROR_LIGHT,
              transform: "scale(1.05)"
            } : {}
          }}>
          <Typography variant="caption" sx={{ fontWeight: 600, opacity: muted ? 0.5 : 1 }}>
            {date.getDate()}
          </Typography>
        </TableCell>
      );
    }

    return (
      <TableCell 
        onClick={handleDayClick}
        sx={{
          verticalAlign: "top",
          padding: 0.5,
          backgroundColor: getBackgroundColor(),
          border: borderStyle,
          height: 96,
          cursor: !muted && oroDisponible !== null ? "pointer" : "default",
          "&:hover": !muted && oroDisponible !== null ? {
            backgroundColor: oroDisponible >= 0 ? "#d4edda" : "#f8d7da",
            transform: "scale(1.02)"
          } : {}
        }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, pt: 0.5 }}>
          <Typography variant="caption" sx={{ opacity: muted ? 0.5 : 1 }}>
            {date.getDate()}
          </Typography>
        </Box>

        <Box sx={{ px: 1, pb: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Tooltip title={`Requerido Oro: ${requeridoOro.toFixed(1)}g | Platino: ${requeridoPlatino.toFixed(1)}g`} arrow>
            <Typography variant="caption" sx={{ color: METALS_CALENDAR_COLORS.NEED_TEXT, fontFamily: "monospace" }}>
              Req: {requeridoOro.toFixed(0)}g | {requeridoPlatino.toFixed(0)}g
            </Typography>
          </Tooltip>
          {oroDisponible !== null ? (
            <Tooltip title={`Disponible Oro: ${oroDisponible.toFixed(1)}g | Platino: ${platinoDisponible.toFixed(1)}g`} arrow>
              <Typography variant="caption" sx={{ 
                color: (oroDisponible >= 0 && platinoDisponible >= 0) 
                  ? METALS_CALENDAR_COLORS.AVAILABLE_POS_TEXT 
                  : METALS_CALENDAR_COLORS.AVAILABLE_NEG_TEXT, 
                fontFamily: "monospace" 
              }}>
                Disp: {oroDisponible.toFixed(0)}g | {platinoDisponible.toFixed(0)}g
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" sx={{ color: METALS_CALENDAR_COLORS.TEXT_MUTED, fontFamily: "monospace" }}>
              Sin datos
            </Typography>
          )}
        </Box>
      </TableCell>
    );
  };

  const DayDetailsPopover = () => (
    <Popover
      open={selectedDay !== null}
      anchorEl={anchorEl}
      onClose={() => {
        setSelectedDay(null);
        setAnchorEl(null);
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        '& .MuiPopover-paper': {
          borderRadius: 2,
          boxShadow: 3,
          border: '1px solid #e0e0e0',
          '&::before': {
            content: '""',
            position: 'absolute',
            right: -8,
            top: 20,
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderLeft: '8px solid #ffffff',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            right: -9,
            top: 20,
            width: 0,
            height: 0,
            borderTop: '9px solid transparent',
            borderBottom: '9px solid transparent',
            borderLeft: '9px solid #e0e0e0',
            zIndex: 0,
          }
        }
      }}
    >
      <Card sx={{ width: 280, borderRadius: 2 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: ESTADO_ORO_COLORS.SUMMARY_TEXT, fontSize: '0.8rem' }}>
              {selectedDay?.date.toLocaleDateString('es-ES', { 
                day: 'numeric', 
                month: 'short' 
              })}
            </Typography>
            <IconButton 
              onClick={() => {
                setSelectedDay(null);
                setAnchorEl(null);
              }}
              size="small"
              sx={{ color: ESTADO_ORO_COLORS.SUMMARY_TEXT, p: 0.5 }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Stock Total */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: ESTADO_ORO_COLORS.SUMMARY_TEXT, fontSize: '0.7rem' }}>
                Stock Total
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Oro:</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'success.main', fontSize: '0.7rem' }}>
                  {selectedDay?.stockTotalOro.toFixed(2)}g
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Platino:</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'success.main', fontSize: '0.7rem' }}>
                  {selectedDay?.stockTotalPlatino.toFixed(2)}g
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* Requerido */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: ESTADO_ORO_COLORS.SUMMARY_TEXT, fontSize: '0.7rem' }}>
                Requerido
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Oro:</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'error.main', fontSize: '0.7rem' }}>
                  {selectedDay?.requeridoOro.toFixed(2)}g
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Platino:</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'error.main', fontSize: '0.7rem' }}>
                  {selectedDay?.requeridoPlatino.toFixed(2)}g
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* Disponible */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: ESTADO_ORO_COLORS.SUMMARY_TEXT, fontSize: '0.7rem' }}>
                Disponible
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Oro:</Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontWeight: 'bold',
                    color: selectedDay?.oroDisponible >= 0 ? 'success.main' : 'error.main',
                    fontSize: '0.7rem'
                  }}
                >
                  {selectedDay?.oroDisponible.toFixed(2)}g
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Platino:</Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontWeight: 'bold',
                    color: selectedDay?.platinoDisponible >= 0 ? 'success.main' : 'error.main',
                    fontSize: '0.7rem'
                  }}
                >
                  {selectedDay?.platinoDisponible.toFixed(2)}g
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Popover>
  );

  return (
    <Paper 
      ref={setCalendarRef}
      elevation={2} 
      sx={{ 
        p: compact ? 1 : 1.5, 
        backgroundColor: BACKGROUND_COLORS.SECONDARY, 
        width: "fit-content", 
        height: fixedHeight ? fixedHeight : 'auto', 
        overflow: 'hidden', 
        marginBottom:"16px"
      }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        {showTitle ? (
          <Typography variant={compact ? "body2" : "subtitle1"} sx={{ 
            fontWeight: "bold", 
            color: METALS_CALENDAR_COLORS.HEADER_TEXT, 
            backgroundColor: METALS_CALENDAR_COLORS.HEADER_BG, 
            px: 1, 
            py: dims.headerPx, 
            borderRadius: 1 
          }}>
            {title}
          </Typography>
        ) : (
          <Typography variant={compact ? "caption" : "subtitle2"} sx={{ fontWeight: 600, color: ESTADO_ORO_COLORS.SUMMARY_TEXT }}>
            {headerMonth}
          </Typography>
        )}
        
        <Box>
          <IconButton onClick={goPrev} size="small" sx={{ color: METALS_CALENDAR_COLORS.NAV_ICON }}>
            <ChevronLeft />
          </IconButton>
          <IconButton onClick={goToday} size="small" sx={{ 
            color: METALS_CALENDAR_COLORS.NAV_ICON, 
            padding: 0.25, 
            minWidth: "auto" 
          }}>
            <Today sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton onClick={goNext} size="small" sx={{ color: METALS_CALENDAR_COLORS.NAV_ICON }}>
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {showTitle && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant={compact ? "caption" : "subtitle2"} sx={{ fontWeight: 600 }}>
            {headerMonth}
          </Typography>
        </Box>
      )}

      <TableContainer>
        <Table size={compact ? "small" : "small"} sx={{ tableLayout: "auto", width: "auto" }}>
          <WeekdayHeader />
          <TableBody>
            {matrix.map((row, i) => (
              <TableRow key={i}>
                {row.map((date) => (
                  <DayCell key={toDateKey(date)} date={date} />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DayDetailsPopover />
    </Paper>
  );
}